import { RoundedFilledButton } from '@/components/buttons/Buttons';
import { copyWith, GenericResponse, ListResponse } from '@/common/common_utils';
import { DateHelper } from '@/common/DateHelper';
import { ENDPOINTS } from '@/constants/endpoints';
import EmptyTable from '@/components/empty/EmptyTable';
import SearchBarInput from '@/components/form-inputs/SearchBarInput';
import DateInput from '@/components/inputs/DateInput';
import { Table } from '@/components/table/Tables';
import { BookingType } from '@/modules/vehicle/engine/ActivityTypes';
import {
	GetPaymentListRequest,
	X4bPaymentDto,
} from '@/modules/vehicle/engine/X4bPaymentDto';
import {
	convertPaymentStatus,
	expandUiStatusesForFilter,
	PAYMENT_STATUSES_UI,
	paymentStatusSeverity,
	type PaymentStatusUi,
} from '@/modules/vehicle/engine/PaymentStatus';
import { XpressVehicleType } from '@/modules/vehicle/engine/VehicleType';
import { axiosInstance } from '@/services/axios.service';
import { X4bEmployeeDetailsDto } from '@/types';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import moment from 'moment';
import { Column } from 'primereact/column';
import { Divider } from 'primereact/divider';
import { Toolbar } from 'primereact/toolbar';
import { classNames } from 'primereact/utils';
import { useState } from 'react';
import { MdFilterAlt, MdToday } from 'react-icons/md';
import style from '../../../views/employee.module.css';
import StatusTag from '../../StatusTag';
import Filters from '../Filters';
import TransactionDetails from './TransactionDetails';

type Props = {
	employee: X4bEmployeeDetailsDto;
};
type OrderRow = {
	Id: string;
	Date: string;
	Time: string;
	Type: string;
	Source: string;
	Status: PaymentStatusUi;
	Amount: number;
};

const defaultTypeFilter = [
	XpressVehicleType.Moto,
	XpressVehicleType.Sedan,
	XpressVehicleType.SUV,
];

const defaultStatusFilter: string[] = [...PAYMENT_STATUSES_UI];

// Note: currently the page is exact copy of same from Finance tab because figma desing is such.
const OrderHistoryTab = (props: Props) => {
	const emp = props.employee;
	const [globalFilter, setGlobalFilter] = useState('');
	const [date, setDate] = useState<moment.Moment | null>(null);
	const [showTransaction, setShowTransaction] = useState<boolean>(false);
	const [selectedPayment, setSelectedPayment] = useState<X4bPaymentDto | null>(
		null,
	);
	const [showFilter, setShowFilter] = useState<boolean>(false);
	const [paginationFilter, setPaginationFilter] = useState({ Skip: 0, Take: 5 });
	const [sort, setSort] = useState({ sortField: '', sortOrder: 0 });
	const [typeFilter, setTypeFilter] =
		useState<XpressVehicleType[]>(defaultTypeFilter);
	const [statusFilter, setStatusFilter] =
		useState<string[]>(defaultStatusFilter);

	const makeListRequest = () => {
		const filters = [];
		if (globalFilter) {
			filters.push({ Name: 'Id', Value: globalFilter });
		}

		const request: GetPaymentListRequest = {
			Skip: paginationFilter.Skip,
			Take: paginationFilter.Take,
			SortField: sort.sortField,
			SortOrder: sort.sortOrder,
			Filters: filters,
			Date: DateHelper.prepareDateToStore(date?.toDate())?.toISOString() ?? null,
			// "Pending" expands to ["PENDING","RESERVED"] so reserved rows are returned
			// when the user filters by Pending. See PaymentStatus.ts.
			Status:
				statusFilter.length < defaultStatusFilter.length
					? expandUiStatusesForFilter(statusFilter as PaymentStatusUi[])
					: null,
			ProgramIds: null,
			Amount: null,
			UserId: emp.Id,
			VehicleTypes:
				typeFilter.length < defaultTypeFilter.length ? typeFilter : null,
		};
		return request;
	};

	const paymentsQ = useQuery({
		queryKey: [
			'Employee.Payments.OrderHistory',
			emp.Id,
			paginationFilter,
			sort,
			globalFilter,
			date?.toISOString(),
			typeFilter,
			statusFilter,
		],
		queryFn: async () => {
			const result = await axiosInstance.post<
				GenericResponse<ListResponse<X4bPaymentDto>>
			>(ENDPOINTS['/api/payments/GetPaymentList'], makeListRequest());
			return result.data.Data;
		},
	});

	const transactions: OrderRow[] =
		paymentsQ.data?.Items?.map((payment) => ({
			Id: payment.Id?.toString() ?? '',
			Date: payment.Date
				? DateHelper.formatDate(new Date(payment.Date))
				: '',
			Time: payment.Date
				? new Date(payment.Date).toLocaleTimeString('en-US', {
					hour: 'numeric',
					minute: '2-digit',
				})
				: '',
			Type: payment.Activity?.VehicleType
				? `Ride - ${XpressVehicleType[payment.Activity.VehicleType]}`
				: 'N/A',
			Source: payment.ProgramName || 'Business Acct',
			Status: convertPaymentStatus(payment.Status),
			Amount: payment.Amount ?? 0,
		})) ?? [];

	const statusTemplate = (rowData: OrderRow) => (
		<StatusTag
			value={rowData.Status}
			severity={paymentStatusSeverity(rowData.Status)}
		/>
	);

	const valueTemplate = (rowData: OrderRow) => (
		<span className="text-title-medium font-normal text-error60">
			-₱{rowData.Amount.toFixed(2)}
		</span>
	);

	const dateTemplate = (rowData: OrderRow) => (
		<div>
			<p className="text-title-medium font-normal text-neutral17">
				{rowData.Date}
			</p>
			<p className="text-body-xx-small font-normal text-neutral17">
				{rowData.Time}
			</p>
		</div>
	);

	const startContent = (
		<SearchBarInput
			placeholder="Search here"
			className="w-[360px] h-14"
			value={globalFilter}
			onChange={(e) => setGlobalFilter(e.target.value)}
		/>
	);

	const endContent = (
		<DateInput
			value={date}
			onChange={setDate}
			variant="outlined"
			icon={(<MdToday size={24} className="text-onSurfaceVariant" />)}
			iconCls="h-10 w-10 p-2 rounded-[100px] bg-[#D5D8DB] pointer-events-none top-2 right-1"
			labelBgColor="bg-surfaceContainerLow"
			containerCls="w-[312px]"
			calendarProps={{
				minYear: 2025,
				maxYear: 2074,
			}}
		/>
	);

	const handleFilterClear = () => {
		setTypeFilter(defaultTypeFilter);
		setStatusFilter(defaultStatusFilter);
	};

	const activeFiltersCount = (() => {
		let count = 0;
		if (typeFilter.length < defaultTypeFilter.length && typeFilter.length > 0) {
			count++;
		}
		if (statusFilter.length < defaultStatusFilter.length) {
			count++;
		}
		return count;
	})();

	const emptyTableTemplate = activeFiltersCount > 0
		? EmptyTable({
			title: 'Sorry we couldn\'t find any results',
			src: '/images/svg/no-filter.svg',
		})
		: EmptyTable({
			title: 'We didn\'t find a match',
			description: 'Try searching for something else.',
			src: '/images/svg/no-result.svg',
			descriptionClassName: 'text-primaryContainer',
		});

	const mapBookingMethod = (bookingType?: BookingType) => {
		switch (bookingType) {
			case BookingType.Scheduled:
				return 'Scheduled booking';
			case BookingType.Flexible:
				return 'Flexible booking';
			case BookingType.Asap:
			default:
				return 'Direct booking by Employee';
		}
	};

	const formatDistance = (distanceInM?: number) => {
		if (!distanceInM) return '-';
		const kilometers = distanceInM / 1000;
		return `${Number.isInteger(kilometers) ? kilometers.toFixed(0) : kilometers.toFixed(1)}km`;
	};

	const mapPaymentToTransaction = (payment: X4bPaymentDto) => {
		const activity = payment.Activity;
		return {
			id: payment.Id ?? '',
			pickup:
				activity?.Pickup?.Name ||
				activity?.Pickup?.PlaceName ||
				'Unknown pickup',
			pickupAddress: activity?.Pickup?.Address || '-',
			dropoff:
				activity?.Dropoff?.Name ||
				activity?.Dropoff?.PlaceName ||
				'Unknown dropoff',
			dropoffAddress: activity?.Dropoff?.Address || '-',
			fare: payment.Amount ?? 0,
			status: convertPaymentStatus(payment.Status),
			duration: DateHelper.formatSeconds(activity?.TimeInSec) || '-',
			distance: formatDistance(activity?.DistanceInM),
			paymentMethod: payment.PaymentMethod || payment.ProgramName || '-',
			bookingMethod: mapBookingMethod(activity?.BookingType),
			driverName: activity?.Driver?.Name || '-',
			driverRating: activity?.Driver?.Rating ?? 0,
			carDetails: activity?.Driver?.Vehicle || '-',
			carPlate: activity?.Driver?.Plate || '-',
		};
	};

	return (
		<>
			<div className="space-y-6">
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
					<div className="p-4 space-y-3 bg-surfaceContainerLow rounded-2xl">
						<div className="flex justify-between items-center">
							<p className="text-label-small font-normal">
								Total Completed
							</p>
							<p className="text-label-small font-normal text-neutral40">
								{emp.TotalCompleted}
							</p>
						</div>
						<div className="flex justify-between items-center">
							<p className="text-label-small font-normal">
								Total Cancelled
							</p>
							<p className="text-label-small font-normal text-neutral40">
								{emp.TotalCancelled}
							</p>
						</div>
					</div>
					<div className="p-4 space-y-3 bg-surfaceContainerLow rounded-2xl">
						<div className="flex justify-between items-center">
							<p className="text-label-small font-normal">
								Most Popular Service:
							</p>
							<p className="text-label-small font-normal text-neutral40">
								Ride - Moto
							</p>
						</div>
						<div className="flex justify-between items-center">
							<p className="text-label-small font-normal">
								Most Popular Partner:
							</p>
							<p className="text-label-small font-normal text-neutral40">
								-
							</p>
						</div>
					</div>
				</div>

				<div
					className={classNames(
						style.employeeTransactionContainer,
						'md:!min-h-[640px]',
					)}
				>
					<div className="flex justify-between items-center">
						<h1 className="text-label-large font-semibold text-neutral17">
							Transactions
						</h1>
						<RoundedFilledButton
							id="filter"
							className={clsx(
								'with-icon-button-primary',
								activeFiltersCount !== 0 &&
								'border-2 border-solid border-orange-600',
							)}
							icon={MdFilterAlt}
							iconSize="18"
							label={`Filter${activeFiltersCount === 0
									? ''
									: `(${activeFiltersCount})`
								}`}
							onClick={() => setShowFilter(!showFilter)}
							width="120px"
							height="40px"
						/>
					</div>
					<div className="">
						{/* Filters */}
						<Toolbar
							pt={{
								root: {
									className: 'people-toolbar',
								},
							}}
							start={startContent}
							end={endContent}
						/>
						<Divider />
						{/* Transactions Table */}
						<Table
							size="small"
							paginator
							emptyMessage={emptyTableTemplate}
							value={transactions}
							rowsPerPageOptions={[5, 10, 20, 30, 40, 50, 100]}
							rows={paginationFilter.Take}
							first={paginationFilter.Skip}
							lazy
							totalRecords={paymentsQ.data?.TotalRecords}
							loading={paymentsQ.isLoading}
							onPage={(event) => {
								setPaginationFilter(
									copyWith(paginationFilter, {
										Skip: event.first,
										Take: event.rows,
									}),
								);
							}}
							sortField={sort.sortField}
							sortOrder={sort.sortOrder as any}
							onSort={(e) => {
								setSort({
									sortField: e.sortField ?? '',
									sortOrder: e.sortOrder ?? 0,
								});
							}}
							globalFilter={globalFilter}
							pt={{
								column: {
									headerCell: {
										className: classNames(
											style.employeeTableHeader,
										),
									},
								},
								bodyRow: {
									className: classNames(style.employeeTable),
								},
								paginator: {
									root: {
										className: classNames(
											style.employeeTablePagination,
										),
									},
								},
							}}
							onRowClick={(e) => {
								const rowData = e.data as OrderRow;
								const payment = paymentsQ.data?.Items?.find(
									(item) => item.Id?.toString() === rowData.Id,
								);
								if (payment) {
									setSelectedPayment(payment);
									setShowTransaction(true);
								}
							}}
							className="md:max-h-[28rem]"
						>
							<Column field="Id" header="TX ID" sortable
								headerClassName="tablet:!w-[80px]"
								bodyClassName="tablet:!w-[80px]"
							/>
							<Column
								field="Date"
								header="Date"
								sortable
								body={dateTemplate}
							/>
							<Column
								field="VehicleType"
								header="TX Type"
								sortable
								body={(rowData: OrderRow) => rowData.Type}
							/>
							<Column
								field="ProgramName"
								header="Source"
								sortable
								headerClassName="tablet:!hidden"
								bodyClassName="tablet:!hidden"
								body={(rowData: OrderRow) => rowData.Source}
							/>
							<Column
								field="Status"
								header="Status"
								body={statusTemplate}
								sortable
							/>
							<Column
								field="Amount"
								header="Value"
								body={valueTemplate}
								sortable
							/>
						</Table>
					</div>
				</div>
			</div>
			{selectedPayment && (
			<TransactionDetails
				visible={showTransaction}
				onHide={() => setShowTransaction(false)}
				transaction={mapPaymentToTransaction(selectedPayment)}
				activity={selectedPayment.Activity}
			/>
			)}
			<Filters
				typeFilter={typeFilter}
				setTypeFilter={setTypeFilter}
				statusFilter={statusFilter}
				setStatusFilter={setStatusFilter}
				visible={showFilter}
				onHide={() => setShowFilter(false)}
				onFilterClear={handleFilterClear}
				type="finance"
			/>
		</>
	);
};

export default OrderHistoryTab;
